import { ChangeDetectionStrategy, Component, WritableSignal, computed, input, signal } from '@angular/core';
import { UserApi } from 'api/user/user-api.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { TagType } from 'lib/ui/tag/index.js';
import { map } from 'rxjs';

import { CoreAppComponent } from 'src/app/core/core-app.component.js';

interface ISelectionTag {
	title: string;
	unit: UserDb.MeasurementUnitType;
	isActive: boolean;
	click: () => void;
}

@Component({
    selector: 'mod-user-preferred-unit',
    templateUrl: './user-preferred-unit.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './user-preferred-unit.component.scss',
    standalone: false
})
export class UserPreferredUnitComponent extends CoreAppComponent {
	public readonly basicTagType = input<TagType>('basic');
	public readonly preferredUnit = signal<UserDb.MeasurementUnitType | undefined>(undefined);

	public readonly selectionTags = computed<ISelectionTag[]>(() => {
		const preferredUnit = this.preferredUnit();

		if (!preferredUnit) {
			return [];
		}

		return [
			{
				title: 'Metric',
				unit: 'metric',
				isActive: preferredUnit === 'metric',
				click: () => {
					this.saveUnit('metric');
				}
			},
			{
				title: 'Imperial',
				unit: 'imperial',
				isActive: preferredUnit === 'imperial',
				click: () => {
					this.saveUnit('imperial');
				}
			}
		];
	});

	private saveUnit(unit: UserDb.MeasurementUnitType): void {
		// update user right away for better responsiveness of UI
		this.preferredUnit.set(unit);

		super.subscribeToObservable(
			this.dependencies.httpService
				.post<UserApi.IUserResponseWithToken, Partial<UserApi.IEditUserPreferredUnitRequest>>((builder) =>
					builder
						.withCustomApiRoute('user', 'editPreferredUnit')
						.withData({
							preferredUnit: unit
						})
						.withId(this.authUserRequired().id)
						.build()
				)
				.pipe(
					map((response) => {
						this.dependencies.authService.updateAuthUser(response.data.token);
						super.notify('Preferred unit updated');
					})
				)
		);
	}

	constructor() {
		super();

		const authUser = this.authUser();

		if (authUser) {
			this.preferredUnit.set(authUser.preferredUnit ?? 'metric');
		}
	}
}
